// InsufficientModal — global einmal in der AppShell gemountet. Lauscht auf das
// `window`-Event 'eve:insufficient-credits' (von eve-runtime bei HTTP 402) und
// zeigt einen Hinweis-Dialog. „Credits kaufen" navigiert auf /credits und
// schließt das Modal. „Schließen" schließt ebenfalls.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CreditCard, X } from 'lucide-react';

/** Event-Name den eve-runtime bei HTTP 402 feuert. */
export const INSUFFICIENT_CREDITS_EVENT = 'eve:insufficient-credits';

export interface InsufficientCreditsDetail {
  needed: number;
  available: number;
}

export function InsufficientModal() {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<InsufficientCreditsDetail | null>(null);

  useEffect(() => {
    const onInsufficient = (e: Event): void => {
      const custom = e as CustomEvent<InsufficientCreditsDetail>;
      const d = custom.detail;
      setDetail({
        needed: typeof d?.needed === 'number' ? d.needed : 0,
        available: typeof d?.available === 'number' ? d.available : 0,
      });
    };
    window.addEventListener(INSUFFICIENT_CREDITS_EVENT, onInsufficient);
    return () => window.removeEventListener(INSUFFICIENT_CREDITS_EVENT, onInsufficient);
  }, []);

  function close(): void {
    setDetail(null);
  }

  function buyCredits(): void {
    close();
    navigate('/credits');
  }

  return (
    <AnimatePresence>
      {detail && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-[min(420px,92vw)] rounded-2xl border border-slate-700/40 bg-[#0a0a0f] p-5"
          >
            <div className="mb-3 flex items-center gap-2">
              <span className="text-amber-400">◆</span>
              <h2 className="text-base font-bold text-slate-100">Credits leer</h2>
            </div>
            <p className="mb-4 text-[13px] text-slate-300">
              Diese Aktion kostet {detail.needed} Credits, du hast {detail.available}.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="rounded-md px-3 py-1.5 text-[12px] text-slate-400 hover:text-slate-200"
              >
                Schließen
              </button>
              <button
                type="button"
                onClick={buyCredits}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-[12px] font-semibold text-amber-300 hover:bg-amber-500/25"
              >
                <CreditCard className="size-3" />
                Credits kaufen
              </button>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Schließen"
              className="absolute right-4 top-4 text-slate-500 hover:text-slate-300"
            >
              <X className="size-4" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
